/**
 * Ce que Hoa dit en passant : les annonces de la régie et ses propres
 * phrases (« Awa rejoint la tribu », « Plus que deux minutes de chat »).
 *
 * Pas de fenêtre par-dessus l'écran : le message prend la bulle de Hoa déjà
 * présente sur l'étape en cours. Le téléphone vibre (Android), la bulle
 * sursaute, son texte s'efface, le message s'écrit, tient le temps d'être
 * lu, s'efface, et le texte de l'étape se réécrit (`BulleVivante`, ui.tsx).
 *
 * Quand l'écran n'a pas de bulle de Hoa, ou qu'une feuille, la carte en
 * grand, la photo ou la couleur plein écran la cachent, une petite bulle de
 * secours passe en haut de l'écran, sans croix.
 *
 * Un appui sur Hoa rejoue la dernière annonce pendant dix minutes.
 *
 * Un petit magasin hors de React, comme les annonces : la bulle porteuse est
 * dessinée par une vingtaine d'écrans qui n'ont pas à savoir d'où vient le
 * message.
 */
import { useEffect, useState } from "react";
import type { Expression } from "./Moai.js";

export type TypeMessage = "annonce" | "hoa";

export interface Message {
  n: number;
  type: TypeMessage;
  texte: string;
  expression: Expression;
  /** Le nom de qui parle : l'organisateur d'un événement. Hoa n'en a pas besoin. */
  auteur: string | null;
  /** Combien de temps le message reste écrit, une fois tapé. */
  tenir: number;
  /** Un message rejoué à la demande ne fait pas vibrer. */
  vibrer: boolean;
}

/** Le premier est celui qu'on dit ; les suivants attendent leur tour. */
let file: Message[] = [];
let n = 0;
const abonnes = new Set<() => void>();

/** Les bulles qui peuvent porter le message, dans l'ordre où elles sont apparues. */
let porteurs: Array<{ id: number; etape: number }> = [];
/** Ce qui cache la bulle : feuille, carte en grand, photo, couleur plein écran. */
let couvertures = 0;
const vibres = new Set<number>();

const REJOUABLE_MS = 10 * 60_000;
let derniere: { texte: string; auteur: string | null; expression: Expression; a: number } | null = null;

function prevenir(): void {
  for (const f of abonnes) f();
}

/** Le temps de lecture d'une annonce : de six à quinze secondes selon sa longueur. */
export function tempsDeLecture(texte: string): number {
  return Math.round(Math.min(15_000, Math.max(6_000, 4_000 + texte.length * 55)));
}

export function dire(m: { type: TypeMessage; texte: string; expression?: Expression; auteur?: string | null; tenir?: number; vibrer?: boolean }): void {
  // Espaces insécables autour de la ponctuation haute : le « ! » ne part
  // pas seul à la ligne pendant que le texte s'écrit.
  const texte = m.texte
    .trim()
    .replace(/ ([!?;:»])/g, "\u202f$1")
    .replace(/« /g, "«\u202f");
  if (!texte) return;
  // Le même message déjà dit ou en attente ne se répète pas.
  if (file.some((x) => x.texte === texte)) return;
  const message: Message = {
    n: ++n,
    type: m.type,
    texte,
    expression: m.expression ?? "surpris",
    auteur: m.auteur ?? null,
    tenir: m.tenir ?? tempsDeLecture(texte),
    vibrer: m.vibrer ?? true,
  };
  if (m.type === "annonce") {
    derniere = { texte, auteur: message.auteur, expression: message.expression, a: Date.now() };
    // Une annonce passe avant les phrases de Hoa qui attendent encore.
    const [courant, ...attente] = file;
    file = courant ? [courant, ...attente.filter((x) => x.type === "annonce"), message, ...attente.filter((x) => x.type !== "annonce")] : [message];
  } else {
    file = [...file, message];
  }
  // Pas plus de quatre en attente : les plus vieilles phrases de Hoa tombent.
  while (file.length > 5) {
    const i = file.findIndex((x, k) => k > 0 && x.type === "hoa");
    file.splice(i > 0 ? i : 1, 1);
  }
  prevenir();
}

/** La bulle a fini de dire ce message : au suivant. */
export function terminer(numero: number): void {
  if (file[0]?.n !== numero) return;
  file = file.slice(1);
  vibres.delete(numero);
  prevenir();
}

/** Faire taire un type de messages, même en cours (on change de soirée, d'événement). */
export function retirer(type: TypeMessage): void {
  const avant = file.length;
  file = file.filter((x) => x.type !== type);
  if (type === "annonce") derniere = null;
  if (file.length !== avant) prevenir();
}

/** Un appui sur Hoa : la dernière annonce, si elle a moins de dix minutes. */
export function rejouerAnnonce(maintenant = Date.now()): boolean {
  if (!derniere || maintenant - derniere.a > REJOUABLE_MS) return false;
  if (file.length > 0) return false;
  const { texte, auteur, expression, a } = derniere;
  dire({ type: "annonce", texte, auteur, expression, vibrer: false });
  // Rejouer ne repousse pas l'échéance.
  if (derniere) derniere.a = a;
  return true;
}

/** La vibration du message, une seule fois, au moment où il s'affiche. */
export function vibrerPour(m: Message): void {
  if (!m.vibrer || vibres.has(m.n)) return;
  vibres.add(m.n);
  try {
    // Android seulement : Safari (iPhone) n'a pas l'API, et ne permet de
    // vibrer qu'après un appui de moins d'une seconde.
    if (typeof navigator.vibrate === "function" && document.visibilityState === "visible") {
      navigator.vibrate(m.type === "annonce" ? [40, 70, 40] : 25);
    }
  } catch {
    /* refusé par le navigateur : le message s'affiche quand même */
  }
}

/* --- Qui porte le message ------------------------------------------ */

let prochainPorteur = 0;

export function inscrirePorteur(etape: number): number {
  const id = ++prochainPorteur;
  porteurs = [...porteurs, { id, etape }];
  prevenir();
  return id;
}

export function desinscrirePorteur(id: number): void {
  porteurs = porteurs.filter((p) => p.id !== id);
  prevenir();
}

export function couvrir(): () => void {
  couvertures++;
  prevenir();
  return () => {
    couvertures--;
    prevenir();
  };
}

/** Ce composant cache la bulle de Hoa tant qu'il est affiché. */
export function useCouvre(actif = true): void {
  useEffect(() => (actif ? couvrir() : undefined), [actif]);
}

export type Pilote = { porteur: number; etape: number } | "secours" | null;

function pilote(): Pilote {
  if (file.length === 0) return null;
  const p = porteurs[0];
  if (!p || couvertures > 0) return "secours";
  return { porteur: p.id, etape: p.etape };
}

export interface EtatMessager {
  courant: Message | null;
  pilote: Pilote;
  /** Un autre message attend derrière celui-ci. */
  suite: boolean;
}

export function etatMessager(): EtatMessager {
  return { courant: file[0] ?? null, pilote: pilote(), suite: file.length > 1 };
}

function memeEtat(a: EtatMessager, b: EtatMessager): boolean {
  const pa = a.pilote;
  const pb = b.pilote;
  const memePilote = pa === pb || (typeof pa === "object" && typeof pb === "object" && pa !== null && pb !== null && pa.porteur === pb.porteur);
  return a.courant?.n === b.courant?.n && memePilote && a.suite === b.suite;
}

export function useMessager(): EtatMessager {
  const [v, setV] = useState<EtatMessager>(etatMessager);
  useEffect(() => {
    const f = (): void =>
      setV((avant) => {
        const apres = etatMessager();
        return memeEtat(avant, apres) ? avant : apres;
      });
    abonnes.add(f);
    f();
    return () => {
      abonnes.delete(f);
    };
  }, []);
  return v;
}

/** L'annonce en cours, s'il y en a une (pour qui n'a besoin que de savoir). */
export function annonceEnCours(): Message | null {
  return file[0]?.type === "annonce" ? file[0] : null;
}

export function abonnerMessager(f: () => void): () => void {
  abonnes.add(f);
  return () => abonnes.delete(f);
}

/** Pour les tests : repartir de zéro. */
export function viderMessager(): void {
  file = [];
  porteurs = [];
  couvertures = 0;
  derniere = null;
  vibres.clear();
  prevenir();
}
